/**
 * PostHog analytics — small wrapper so screens import a stable surface
 * regardless of whether PostHog is configured at runtime.
 *
 * Init is lazy: if `VITE_POSTHOG_KEY` is unset, all `track*()` calls become
 * silent no-ops and no SDK is loaded. This keeps local dev / static-deploy
 * builds free of analytics noise unless the key is provided.
 *
 * Memory note (Tappya): EU PostHog instance, key `phc_FujogbRpAK4nX9gSoQb2cDqg9RHF6L6a9VbGKiI98t`,
 * unified project 78456. Override via `VITE_POSTHOG_HOST` / `VITE_POSTHOG_KEY`.
 */
import posthog from "posthog-js";

const PH_KEY =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ?? "";
const PH_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
  "https://eu.i.posthog.com";

let initialised = false;

export function initAnalytics(): void {
  if (initialised) return;
  if (!PH_KEY) return; // not configured — stay silent
  if (typeof window === "undefined") return;
  // Local dev must not land in the shared production project.
  if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) return;
  posthog.init(PH_KEY, {
    api_host: PH_HOST,
    // Identify anonymous visitors so we don't drop pre-signup funnel data.
    person_profiles: "always",
    capture_pageview: false, // we'll send manually on route change
    autocapture: false,
    // Session replay: OFF unless the visit turns out to matter (see REPLAY_TRIGGERS).
    // Storefront traffic is large and checkout shows names, phones and addresses, so
    // recordings start only on trouble (or a 10% sample of checkouts) and mask ALL text:
    // layout, taps and scrolling stay visible, customer data does not.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
    loaded: () => {
      // Tag every event with the source so the unified project can split.
      // Staging and prod share one PostHog project; app_env lets every chart filter.
      const h = window.location.hostname;
      const app_env = /localhost|127\.0\.0\.1/.test(h) ? "local" : h.includes("staging") ? "staging" : "prod";
      posthog.register({ source: "online_ordering_v2", app_env });
    },
  });
  initialised = true;
}

/** Events that make a storefront session worth watching: replay starts on the first one. */
const REPLAY_TRIGGERS = new Set([
  "order_failed",
  "order_append_failed",
  "coverage_blocked",
  "promo_code_rejected",
]);
/** Share of ordinary checkouts recorded, so normal buying behaviour is visible too. */
const CHECKOUT_REPLAY_SAMPLE = 0.1;
let replayStarted = false;

/** Start recording this session (once), tagging why so replays can be filtered by reason. */
export function recordThisSession(reason: string): void {
  if (!initialised || replayStarted) return;
  replayStarted = true;
  try {
    posthog.register({ replay_reason: reason });
    // Typed through a cast: the method exists at runtime in every posthog-js 1.x, but which
    // declaration the default export resolves to moves between releases (CI's `npm install`
    // picks the newest ^1.x), and a direct call failed the build once (2026-10-13).
    (posthog as unknown as { startSessionRecording?: () => void }).startSessionRecording?.();
  } catch {
    /* never let analytics throw */
  }
}

if (typeof window !== "undefined") {
  // An uncaught error in the storefront is always worth a recording.
  window.addEventListener("error", () => recordThisSession("js_error"));
  window.addEventListener("unhandledrejection", () => recordThisSession("js_error"));
}

/** Safe wrapper — no-op when PostHog is not configured. */
export function track(
  event: string,
  props?: Record<string, unknown>,
): void {
  if (!initialised) return;
  if (REPLAY_TRIGGERS.has(event)) recordThisSession(event);
  else if (event === "checkout_started" && Math.random() < CHECKOUT_REPLAY_SAMPLE) recordThisSession("checkout_sample");
  try {
    posthog.capture(event, props);
  } catch {
    /* never let analytics throw */
  }
}

/** Identify a customer once they've signed in. */
export function identify(
  customerId: string,
  traits?: Record<string, unknown>,
): void {
  if (!initialised) return;
  try {
    posthog.identify(customerId, traits);
  } catch {
    /* swallow */
  }
}

/** Reset identity (sign out). */
export function resetAnalytics(): void {
  if (!initialised) return;
  try {
    posthog.reset();
  } catch {
    /* swallow */
  }
}

/** Group the current user under a tenant (for company-level metrics). */
export function setTenantGroup(tenantId: string): void {
  if (!initialised) return;
  try {
    posthog.group("company", tenantId);
  } catch {
    /* swallow */
  }
}

/** Page view — call from a router effect. */
export function trackPageView(path: string): void {
  track("$pageview", { $current_url: window.location.href, path });
}
